import React, { useState, useEffect, useRef } from 'react'
import { 
  ChevronDownIcon, 
  XMarkIcon, 
  PlusIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import { Label } from '@/types'
import { Transition } from '@headlessui/react'
import { getApiUrl, createFetchConfig } from '../config/api'

interface LabelPickerProps {
  value: Label[]
  onChange: (labels: Label[]) => void
  projectId: string
  disabled?: boolean
  placeholder?: string
}

const LabelPicker: React.FC<LabelPickerProps> = ({
  value = [],
  onChange,
  projectId,
  disabled = false,
  placeholder = "選擇標籤..."
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [allLabels, setAllLabels] = useState<Label[]>([])
  const [recentLabels, setRecentLabels] = useState<Label[]>([])
  const [suggestedLabels, setSuggestedLabels] = useState<Label[]>([])
  const [loading, setLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  const [newLabelColor, setNewLabelColor] = useState('#6b7280')
  
  const searchInputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom')

  // 預設顏色選項
  const colorOptions = [
    '#d73a4a', '#0075ca', '#0052cc', '#fbca04', '#a2eeef',
    '#b60205', '#ff9800', '#9c27b0', '#1976d2', '#4caf50',
    '#9e9e9e', '#ff5722', '#fdd835', '#795548', '#00bcd4',
    '#e91e63', '#607d8b', '#ff6b6b', '#673ab7', '#009688'
  ]

  // 關閉下拉選單
  const closeDropdown = () => {
    setIsOpen(false)
    setSearchQuery('')
    setIsCreating(false)
    setNewLabelName('')
  }

  // 計算下拉選單位置
  const calculateDropdownPosition = () => {
    if (!containerRef.current) return

    const container = containerRef.current
    const rect = container.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    
    // 預估下拉選單高度（大約320px）
    const dropdownHeight = 320
    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top
    
    // 如果下方空間不足且上方空間充足，則向上展開
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      setDropdownPosition('top')
    } else {
      setDropdownPosition('bottom')
    }
  }

  // 點擊外部關閉
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // 獲取標籤數據
  const fetchLabels = async () => {
    setLoading(true)
    try {
      // 並行獲取所有標籤、最近使用標籤和建議標籤
      const [labelsRes, recentRes, suggestedRes] = await Promise.all([
        fetch(getApiUrl(`labels?search=${searchQuery}`), createFetchConfig()),
        fetch(getApiUrl('labels/recent'), createFetchConfig()),
        fetch(getApiUrl(`labels/suggestions?projectId=${projectId}&limit=5`), createFetchConfig())
      ])

      if (labelsRes.ok) {
        const labelsData = await labelsRes.json()
        setAllLabels(labelsData.labels || [])
      }

      if (recentRes.ok) {
        const recentData = await recentRes.json()
        setRecentLabels(recentData.labels || [])
      }

      if (suggestedRes.ok) {
        const suggestedData = await suggestedRes.json()
        setSuggestedLabels(suggestedData.labels || [])
      }
    } catch (error) {
      console.error('Error fetching labels:', error)
    } finally {
      setLoading(false)
    }
  }

  // 開啟下拉選單時獲取數據
  useEffect(() => {
    if (isOpen) {
      fetchLabels()
      // 聚焦搜尋輸入框
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, searchQuery, projectId])

  // 切換標籤選擇
  const toggleLabel = (label: Label) => {
    console.log('toggleLabel called with:', label.name, label.id)
    const isSelected = value.some(l => l.id === label.id)
    console.log('isSelected:', isSelected)
    
    if (isSelected) {
      const newValue = value.filter(l => l.id !== label.id)
      console.log('Removing label, new value:', newValue)
      onChange(newValue)
    } else {
      const newValue = [...value, label]
      console.log('Adding label, new value:', newValue)
      onChange(newValue)
    }
  }

  // 移除標籤
  const removeLabel = (labelId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    onChange(value.filter(l => l.id !== labelId))
  }

  // 建立新標籤
  const createNewLabel = async () => {
    if (!newLabelName.trim()) return

    try {
      const response = await fetch(getApiUrl('labels'), createFetchConfig({
        method: 'POST',
        body: JSON.stringify({
          name: newLabelName.trim(),
          color: newLabelColor
        })
      }))

      if (response.ok) {
        const newLabel = await response.json()
        // 將新標籤添加到選中的標籤中
        onChange([...value, newLabel])
        // 重新獲取標籤列表
        fetchLabels()
        // 重置表單
        setNewLabelName('')
        setNewLabelColor('#6b7280')
        setIsCreating(false)
      } else {
        console.error('Failed to create label')
      }
    } catch (error) {
      console.error('Error creating label:', error)
    }
  }

  // 過濾標籤
  const filteredLabels = allLabels.filter(label => 
    label.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !value.some(v => v.id === label.id)
  )

  // 過濾最近使用的標籤（排除已選中的）
  const filteredRecentLabels = recentLabels.filter(label => 
    !value.some(v => v.id === label.id) &&
    label.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // 過濾建議標籤（排除已選中的和最近使用的）
  const filteredSuggestedLabels = suggestedLabels.filter(label => 
    !value.some(v => v.id === label.id) &&
    !recentLabels.some(r => r.id === label.id) &&
    label.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="relative" ref={containerRef}>
      {/* 選擇器觸發器 */}
      <div
        onClick={() => {
          if (!disabled) {
            if (!isOpen) {
              calculateDropdownPosition()
            }
            setIsOpen(!isOpen)
          }
        }}
        className={`min-h-[38px] p-2 border rounded-lg bg-white cursor-pointer transition-colors ${
          disabled 
            ? 'bg-gray-50 cursor-not-allowed border-gray-200' 
            : isOpen 
              ? 'border-blue-500 ring-1 ring-blue-500' 
              : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <div className="flex items-center flex-wrap gap-1">
          {/* 已選擇的標籤 */}
          {value.length > 0 ? (
            value.map((label) => (
              <span
                key={label.id}
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                style={{
                  backgroundColor: label.color + '20',
                  color: label.color,
                }}
              >
                {label.name}
                {!disabled && (
                  <button
                    onClick={(e) => removeLabel(label.id, e)}
                    className="ml-1 hover:bg-black hover:bg-opacity-10 rounded"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          ) : (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          )}
          
          {/* 下拉箭頭 */}
          {!disabled && (
            <ChevronDownIcon 
              className={`ml-auto h-4 w-4 text-gray-400 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`} 
            />
          )}
        </div>
      </div>

      {/* 下拉選單 */}
      <Transition
        show={isOpen}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <div className={`absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden ${
          dropdownPosition === 'top' 
            ? 'bottom-full mb-1' 
            : 'top-full mt-1'
        }`}>
          {/* 搜尋框 */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="搜尋標籤..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {/* 新建標籤區域 */}
            {searchQuery && !filteredLabels.some(l => l.name.toLowerCase() === searchQuery.toLowerCase()) && (
              <div className="p-3 border-b border-gray-100">
                {isCreating ? (
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="標籤名稱"
                      value={newLabelName}
                      onChange={(e) => setNewLabelName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">顏色:</span>
                      <div className="flex space-x-1">
                        {colorOptions.slice(0, 10).map((color) => (
                          <button
                            key={color}
                            onClick={() => setNewLabelColor(color)}
                            className={`w-6 h-6 rounded border-2 ${
                              newLabelColor === color ? 'border-gray-800' : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={createNewLabel}
                        className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                        disabled={!newLabelName.trim()}
                      >
                        建立
                      </button>
                      <button
                        onClick={() => setIsCreating(false)}
                        className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setIsCreating(true)
                      setNewLabelName(searchQuery)
                    }}
                    className="flex items-center space-x-2 w-full text-left px-3 py-2 hover:bg-gray-50 rounded"
                  >
                    <PlusIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">建立標籤 "{searchQuery}"</span>
                  </button>
                )}
              </div>
            )}

            {/* 最近使用的標籤 */}
            {filteredRecentLabels.length > 0 && !searchQuery && (
              <div className="p-3 border-b border-gray-100">
                <div className="text-xs font-medium text-gray-500 mb-2">最近使用</div>
                <div className="space-y-1">
                  {filteredRecentLabels.map((label) => (
                    <LabelOption
                      key={label.id}
                      label={label}
                      isSelected={value.some(v => v.id === label.id)}
                      onClick={() => toggleLabel(label)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* 建議標籤 */}
            {filteredSuggestedLabels.length > 0 && !searchQuery && (
              <div className="p-3 border-b border-gray-100">
                <div className="text-xs font-medium text-gray-500 mb-2">建議標籤</div>
                <div className="space-y-1">
                  {filteredSuggestedLabels.map((label) => (
                    <LabelOption
                      key={label.id}
                      label={label}
                      isSelected={value.some(v => v.id === label.id)}
                      onClick={() => toggleLabel(label)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* 所有標籤 */}
            <div className="p-3">
              <div className="text-xs font-medium text-gray-500 mb-2">
                {searchQuery ? '搜尋結果' : '所有標籤'}
              </div>
              <div className="space-y-1">
                {loading ? (
                  <div className="text-center py-4 text-gray-500">載入中...</div>
                ) : filteredLabels.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    {searchQuery ? '找不到相符的標籤' : '目前沒有可用的標籤'}
                  </div>
                ) : (
                  filteredLabels.map((label) => (
                    <LabelOption
                      key={label.id}
                      label={label}
                      isSelected={value.some(v => v.id === label.id)}
                      onClick={() => toggleLabel(label)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  )
}

// 標籤選項元件
interface LabelOptionProps {
  label: Label
  isSelected: boolean
  onClick: () => void
}

const LabelOption: React.FC<LabelOptionProps> = ({ label, isSelected, onClick }) => {
  const handleClick = () => {
    console.log('LabelOption clicked:', label.name, label.id)
    onClick()
  }

  return (
    <div
      onClick={handleClick}
      className={`flex items-center space-x-2 px-3 py-2 rounded cursor-pointer transition-colors ${
        isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
    >
      <div
        className="w-3 h-3 rounded"
        style={{ backgroundColor: label.color }}
      />
      <span className="text-sm text-gray-800 flex-1">{label.name}</span>
      {isSelected && (
        <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-white rounded-full" />
        </div>
      )}
    </div>
  )
}

export default LabelPicker
